import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useEffect } from 'react';
import { useCollapse } from 'react-collapsed';

import { useTimer } from '@/hooks';
import { useAuthStore, useModalStore } from '@/store';
import { ChosenNumber } from '@/types';
import { formatNumber, formatNumberToStr, formatTime, getRemainingSeconds } from '@/utils';

import { ArrowDownIcon } from '../../icons/main-block';
import cn from './styles.module.scss';
import { TariffCarousel } from './TariffCarousel/TariffCarousel.tsx';

interface INumberBlockProps {
    onClick?: (chosenNumber: ChosenNumber) => void;
    number: string;
    tariffMin: number;
    operator: string;
    region: string;
    tariffs: string[];
    isMyReservations?: boolean;
    expiry?: number;
    numberCost: number;
}

export const NumberBlock = ({
    onClick,
    number,
    tariffMin,
    region,
    operator,
    tariffs,
    isMyReservations,
    expiry,
    numberCost,
}: INumberBlockProps) => {
    const { token, isAuth } = useAuthStore();
    const { getToggleProps, getCollapseProps, isExpanded } = useCollapse();
    const { setIsOpen, setShowSignUp } = useModalStore();

    const queryClient = useQueryClient();

    const { time, start } = useTimer(getRemainingSeconds(expiry + '000'));

    const toggleProps = getToggleProps();

    const formattedNumber = formatNumberToStr(formatNumber(number, true));

    const handleClick = () => {
        setShowSignUp(false);
        setIsOpen(true);
    };

    useEffect(() => {
        if (number) start();
    }, [number]);

    useEffect(() => {
        if (time <= 0) {
            queryClient.invalidateQueries({ queryKey: ['reservations', token] });
        }
    }, [time]);

    return (
        <div className={cn.numBlockContainer}>
            <div
                className={cn.numBlockHead}
                {...toggleProps}
                onClick={isAuth ? toggleProps.onClick : handleClick}
            >
                <div className={cn.imgWrapper}>
                    <img src={`/images/icons/operators/${operator}.svg`} alt="" />
                </div>
                <div className={cn.numberInfo}>
                    <div className={cn.number}>
                        {formattedNumber}
                        <span className={clsx(cn.arrowIcon, isExpanded && cn.active)}>
                            {ArrowDownIcon}
                        </span>
                    </div>
                    <div className={cn.tariffs}>
                        {isMyReservations
                            ? `До конца брони: ${formatTime(time)}`
                            : `Тарифы от ${tariffMin} ₽`}
                    </div>
                </div>
                <div className={cn.numberRegion}>{region}</div>
            </div>
            <div {...getCollapseProps()} className={cn.collapseContainer}>
                <TariffCarousel
                    region={region}
                    operator={operator || ''}
                    number={formattedNumber}
                    tariffs={tariffs}
                    onClick={onClick}
                    minTariffPrice={tariffMin}
                    numberCost={numberCost}
                />
            </div>
        </div>
    );
};
